/**
 * Zentrale Definitionen für Schulklassen ("grade") inkl. der Sonderformen
 * Vorklasse und Paktklasse.
 *
 * Hintergrund (Issue #604):
 * - **Vorklasse**: sequenziell VOR der 1. Klasse. Wird als `startingGrade`/
 *   `currentGrade` = 0 abgebildet und rückt beim Schuljahreswechsel ganz
 *   normal in die 1. Klasse auf (`grade + 1`). `classType` bleibt 'regular'.
 * - **Paktklasse**: eine eigene Klassenform ("Pakt für den Ganztag"). Pro
 *   Schule über das Flag `offersPaktklasse` freischaltbar (siehe
 *   `isPaktklasseAllowedForSchool`). Sie läuft wie eine reguläre
 *   Grundschulklasse 4 Jahre (intern `grade` 1..4) und endet danach. Die
 *   Besonderheit: die Kinder bleiben 4 Jahre im selben Klassenverbund —
 *   deshalb wird durchgehend das Label "Paktklasse" angezeigt (nicht
 *   "2. Klasse" etc.) und die Klasse ist bei der Verlängerung nicht änderbar.
 *
 * WARNING: Die Label-Logik (`getGradeLabel`) ist nach
 * `functions/src/_shared/grade-utils.ts` kopiert. Bei Änderungen synchron
 * halten!
 */

export type ClassType = 'regular' | 'paktklasse';

/**
 * Ob die Paktklasse für die gegebene Schule als Auswahloption angeboten wird.
 * Wird pro Schule über das Flag `offersPaktklasse` gesteuert (Issue #604) —
 * Admins schalten es im Schul-Editor frei. So sind schulindividuelle
 * Ausnahmen möglich (eine Schule im selben Landkreis ohne Paktklasse o.ä.).
 */
export function isPaktklasseAllowedForSchool(
  school: { offersPaktklasse?: boolean } | undefined | null
): boolean {
  return school?.offersPaktklasse === true;
}

/** Numerischer Wert der Vorklasse (vor der 1. Klasse). */
export const GRADE_VORKLASSE = 0;

/** Kleinster gültiger grade-Wert (Vorklasse). */
export const GRADE_MIN = 0;

/** Größter grade-Wert in der Grundschule (4. Klasse). */
export const GRADE_MAX = 4;

/**
 * UI-Sentinel für die Paktklasse im Auswahl-Dropdown.
 *
 * Die Paktklasse braucht im flachen Dropdown einen eigenen Eintrag, wird in
 * der Datenbank aber über `classType: 'paktklasse'` + numerischem `grade`
 * gespeichert. Dieser Wert existiert NUR im FormControl, niemals in Firestore.
 */
export const PAKTKLASSE_FORM_VALUE = -1;

/**
 * Optionen für das Klassen-Auswahl-Dropdown (Anmeldung & Vertragserstellung).
 * Reihenfolge gemäß Kundenwunsch: Vorklasse → 1.–4. Klasse → Paktklasse.
 */
export const ENROLLMENT_GRADE_OPTIONS: ReadonlyArray<{
  value: number;
  label: string;
}> = [
  { value: GRADE_VORKLASSE, label: 'Vorklasse' },
  { value: 1, label: '1. Klasse' },
  { value: 2, label: '2. Klasse' },
  { value: 3, label: '3. Klasse' },
  { value: 4, label: '4. Klasse' },
  { value: PAKTKLASSE_FORM_VALUE, label: 'Paktklasse' },
];

/**
 * Liefert das anzuzeigende Label für eine Klasse.
 * - Paktklasse → "Paktklasse" (unabhängig vom numerischen grade)
 * - grade 0 → "Vorklasse"
 * - grade 1..n → "n. Klasse"
 */
export function getGradeLabel(
  grade: number | null | undefined,
  classType: ClassType = 'regular'
): string {
  if (classType === 'paktklasse') {
    return 'Paktklasse';
  }
  if (grade === GRADE_VORKLASSE) {
    return 'Vorklasse';
  }
  if (grade == null) {
    return '';
  }
  return `${grade}. Klasse`;
}

/**
 * Wandelt den (Sentinel-behafteten) Dropdown-Wert in die persistierbaren
 * Felder `startingGrade` + `classType` um.
 */
export function resolveGradeSelection(formValue: number): {
  grade: number;
  classType: ClassType;
} {
  if (formValue === PAKTKLASSE_FORM_VALUE) {
    // Paktklasse beginnt immer im 1. Jahr des 4-jährigen Klassenverbunds.
    return { grade: 1, classType: 'paktklasse' };
  }
  return { grade: formValue, classType: 'regular' };
}

/**
 * Wandelt den persistierten Zustand (`grade` + `classType`) in den
 * Dropdown-Wert (inkl. Paktklasse-Sentinel) zurück.
 */
export function toGradeFormValue(
  grade: number | null | undefined,
  classType: ClassType | undefined
): number | null {
  if (classType === 'paktklasse') {
    return PAKTKLASSE_FORM_VALUE;
  }
  return grade ?? null;
}
